/**
 * File Attachment Rules (FAR) — API client.
 *
 * Endpoints are documented in FAR_BACKEND_REQUIREMENTS.md (repo root).
 * Set REACT_APP_FAR_MOCK=true in .env to run the UI against in-memory sample
 * data until the backend endpoints are available. Never enable it in production.
 */
import axios from "axios";
import Config from "../../../../../Config";
import {
    FAR_DEFAULT_SETTINGS,
    normalizeRule,
    exclusionHash
} from "./farEngine";

export const isFarMockMode = () => {
    try{
        return process.env.REACT_APP_FAR_MOCK === "true";
    }catch(e){ }
    return false;
};

const unwrap = (res) => {
    const data = res && res.data ? res.data : {};
    if(data.code === undefined || data.code === 200){
        return data.body !== undefined ? data.body : data;
    }
    throw (data.body || { message: "Invalid response from api." });
};

const unwrapError = (err) => {
    if(err && err.response && typeof(err.response) === "object" && err.response.data){
        return err.response.data.body || err.response.data;
    }
    return err;
};

const request = (method, path, { data = null, params = null, source = null } = {}) => {
    const options = {
        url: Config.getApiUrl()+path,
        method: method,
        headers: Config.getApiHeaders()
    };
    if(data !== null){ options.data = JSON.stringify(data); }
    if(params !== null){ options.params = params; }
    if(source !== null){ options.cancelToken = source.token; }
    return axios(options).then(unwrap).catch(err => { throw unwrapError(err); });
};

/*
 * Serializes a normalized rule for the API. Legacy keys are kept so the Outlook plug-in tables stay compatible.
 * To/CC/BCC are not sent: the web portal no longer shows them. The server keeps the stored
 * values on edit and inspects all three on new rules (they only matter to the Outlook plug-in).
 * Recipient exclusions are only sent when the editor loaded them (blockedListsVisible or a
 * new rule), so saving never wipes lists the user could not see.
 */
export const serializeRule = (rule) => Object.assign({
    name: rule.name,
    description: rule.description,
    active: rule.active ? 1 : 0,
    prefix: rule.prefix,
    prefixOp: rule.prefixOp,
    suffix: rule.suffix,
    suffixOp: rule.suffixOp,
    directory: rule.directory,
    sendlinxFolder: rule.sendlinxFolder || "",
    emails: rule.emails,
    case: 0
}, (!rule.id || rule.blockedListsVisible) ? {
    blocked_emails: rule.blockedEmails || [],
    blocked_domains: rule.blockedDomains || [],
    exclusion_action: rule.exclusionAction || "block"
} : {});

const normalizeRuleList = (body) => {
    const list = Array.isArray(body) ? body : (body && Array.isArray(body.response) ? body.response : []);
    return list.map(normalizeRule).filter(rule => rule !== null);
};

/* ------------------------------------------------------------------ */
/* Mock store (REACT_APP_FAR_MOCK=true only)                           */
/* ------------------------------------------------------------------ */
const mockNow = () => new Date().toISOString();
const mockDb = {
    nextId: 9,
    settings: Object.assign({}, FAR_DEFAULT_SETTINGS),
    rules: [
        { id: 1, name: "ABC Inc Invoices", description: "Customer invoices to AP", active: 1, prefix: "inv", prefix_op: 1, suffix: "", suffix_op: 0, directory: "C:\\customers\\ABC_Inc\\invoices\\", emails: ["ap@abcinc.com"], to: 1, cc: 1, bcc: 1, updated_at: "2026-09-20T10:00:00Z", updated_by_name: "Matt Fera" },
        { id: 9, name: "ABC Inc folder", description: "Anything in the ABC customer folder in My Docs", active: 1, prefix: "", prefix_op: 0, suffix: "", suffix_op: 0, directory: "", sendlinx_folder: "/My Docs/Customers/ABC Inc", emails: ["ap@abcinc.com", "@abcinc.com"], to: 1, cc: 1, bcc: 1, updated_at: "2026-10-19T10:00:00Z", updated_by_name: "Matt Fera" },
        { id: 2, name: "Invoice Prefix", description: "Any file starting with inv", active: 1, prefix: "inv", prefix_op: 0, suffix: "", suffix_op: 0, directory: "", emails: ["ap@abcinc.com"], to: 1, cc: 1, bcc: 1, updated_at: "2026-08-15T10:00:00Z", updated_by_name: "Matt Fera" },
        { id: 3, name: "Invoice ABC Suffix", description: "Inv + suffix and directory", active: 1, prefix: "", prefix_op: 0, suffix: "_abc", suffix_op: 1, directory: "C:\\customers\\ABC_Inc\\invoices\\", emails: ["ap@abcinc.com"], to: 1, cc: 1, bcc: 1, updated_at: "2026-08-10T10:00:00Z", updated_by_name: "Matt Fera" },
        { id: 4, name: "Stock Reports", description: "Send stock reports to main mailbox", active: 1, prefix: "stockreport", prefix_op: 0, suffix: "", suffix_op: 0, directory: "", emails: ["stockreports@xyz.com"], to: 1, cc: 1, bcc: 0, updated_at: "2026-07-28T10:00:00Z", updated_by_name: "Matt Fera" },
        { id: 5, name: "HR Confidential", description: "HR files restricted", active: 0, prefix: "", prefix_op: 0, suffix: "", suffix_op: 0, directory: "C:\\HR\\Confidential", emails: ["hr@company.com"], to: 1, cc: 1, bcc: 1, updated_at: "2026-06-12T10:00:00Z", updated_by_name: "Matt Fera" },
        { id: 6, name: "Project Specs", description: "Project specification documents", active: 1, prefix: "", prefix_op: 0, suffix: "_specs", suffix_op: 1, directory: "C:\\Projects\\Specs", emails: ["pm@company.com"], to: 1, cc: 1, bcc: 0, updated_at: "2026-06-01T10:00:00Z", updated_by_name: "Matt Fera" },
        { id: 7, name: "Financial Reports", description: "Quarterly financials", active: 1, prefix: "finance", prefix_op: 1, suffix: "_q", suffix_op: 0, directory: "", emails: ["cfo@company.com"], to: 1, cc: 1, bcc: 1, updated_at: "2026-05-20T10:00:00Z", updated_by_name: "Matt Fera" },
        { id: 8, name: "Customer Contracts", description: "Contracts for customer review", active: 1, prefix: "contract", prefix_op: 1, suffix: "", suffix_op: 0, directory: "C:\\Customers\\", emails: ["legal@company.com", "@client.com"], to: 1, cc: 1, bcc: 0, updated_at: "2026-05-05T10:00:00Z", updated_by_name: "Matt Fera",
            blocked_emails: ["ceo@competitor.com", "legal@rival.io"], blocked_domains: ["competitor.com"], exclusion_action: "block" }
    ],
    audit: [
        { id: 4, created_at: "2026-10-09T08:10:00Z", user_name: "Matt Fera", user_email: "matt.george@dtrsolutions.com", file_name: "contract_acme.pdf", file_path: "", rule_id: 8, rule_name: "Customer Contracts", recipient: "sales@competitor.com", field: "to", result: "excluded", exclusion_type: "domain", exclusion_action: "block", action: "blocked", reason: "Recipient exclusion: blocked domain competitor.com", transfer_id: "" },
        { id: 3, created_at: "2026-10-08T14:22:00Z", user_name: "Matt Fera", user_email: "matt.george@dtrsolutions.com", file_name: "Inv-637_abc.pdf", file_path: "", rule_id: 1, rule_name: "ABC Inc Invoices", recipient: "ap@abc_usa.com", field: "to", result: "unauthorized", action: "exception_authorized", reason: "Customer requested copy to US office", transfer_id: "T-10422" },
        { id: 2, created_at: "2026-10-08T11:05:00Z", user_name: "Matt Fera", user_email: "matt.george@dtrsolutions.com", file_name: "Inv-640_abc.pdf", file_path: "", rule_id: 1, rule_name: "ABC Inc Invoices", recipient: "billing@other.com", field: "to", result: "unauthorized", action: "recipient_removed", reason: "", transfer_id: "T-10419" },
        { id: 1, created_at: "2026-10-07T09:41:00Z", user_name: "Matt Fera", user_email: "matt.george@dtrsolutions.com", file_name: "stockreport_oct.xlsx", file_path: "", rule_id: 4, rule_name: "Stock Reports", recipient: "stockreports@xyz.com", field: "to", result: "authorized", action: "sent", reason: "", transfer_id: "T-10401" }
    ]
};
const mockDelay = (value) => new Promise(resolve => setTimeout(() => resolve(JSON.parse(JSON.stringify(value))), 250));
/* Same extra keys as FarService::ruleRows (the mock user is an administrator, so lists are included). */
const mockRow = (row) => {
    const emails = row.blocked_emails || [];
    const domains = row.blocked_domains || [];
    return Object.assign({ exclusion_action: "block" }, row, {
        blocked_emails: emails, blocked_domains: domains,
        blocked_emails_count: emails.length, blocked_domains_count: domains.length, has_exclusions: emails.length + domains.length > 0,
        blocked_email_hashes: emails.map(exclusionHash), blocked_domain_hashes: domains.map(exclusionHash), editable: true
    });
};

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */
const FarApi = {
    /** Send page bootstrap: { enabled, settings, rules[], canOverride } */
    getConfig(source = null){
        if(isFarMockMode()){
            return mockDelay({ enabled: true, settings: mockDb.settings, rules: mockDb.rules.filter(r => r.active).map(mockRow), canOverride: true, isAdmin: true }).then(body => ({
                enabled: true,
                settings: Object.assign({}, FAR_DEFAULT_SETTINGS, body.settings),
                rules: normalizeRuleList(body.rules),
                canOverride: body.canOverride,
                isAdmin: body.isAdmin
            }));
        }
        return request("GET", "file/attachment/config", { source }).then(body => ({
            enabled: !!(body && body.enabled),
            settings: Object.assign({}, FAR_DEFAULT_SETTINGS, (body && body.settings) || {}),
            rules: normalizeRuleList(body && body.rules),
            canOverride: body && body.canOverride !== undefined ? !!body.canOverride : true,
            isAdmin: !!(body && body.isAdmin)
        }));
    },
    getRules(params = {}, source = null){
        if(isFarMockMode()){
            return mockDelay(mockDb.rules.map(mockRow)).then(normalizeRuleList);
        }
        const query = Object.assign({ page: 1, limit: 500 }, params);
        return request("GET", "file/attachment/rule", { params: query, source }).then(normalizeRuleList);
    },
    saveRule(rule, source = null){
        const payload = serializeRule(rule);
        if(isFarMockMode()){
            const row = {
                id: rule.id || mockDb.nextId++,
                name: payload.name, description: payload.description, active: payload.active,
                prefix: payload.prefix, prefix_op: payload.prefixOp, suffix: payload.suffix, suffix_op: payload.suffixOp,
                directory: payload.directory, sendlinx_folder: payload.sendlinxFolder, emails: payload.emails, to: 1, cc: 1, bcc: 1,
                updated_at: mockNow(), updated_by_name: "You"
            };
            const index = mockDb.rules.findIndex(r => r.id === row.id);
            const previous = index >= 0 ? mockDb.rules[index] : {};
            row.blocked_emails = payload.blocked_emails !== undefined ? payload.blocked_emails : (previous.blocked_emails || []);
            row.blocked_domains = payload.blocked_domains !== undefined ? payload.blocked_domains : (previous.blocked_domains || []);
            row.exclusion_action = payload.exclusion_action || previous.exclusion_action || "block";
            if(index >= 0){ mockDb.rules[index] = row; }else{ mockDb.rules.unshift(row); }
            return mockDelay(mockRow(row)).then(normalizeRule);
        }
        if(rule.id){
            return request("PUT", "file/attachment/rule/"+rule.id, { data: payload, source }).then(body => normalizeRule(body && body.id ? body : Object.assign({}, rule)));
        }
        return request("POST", "file/attachment/rule", { data: payload, source }).then(body => normalizeRule(body && body.id ? body : Object.assign({}, rule)));
    },
    deleteRule(id){
        if(isFarMockMode()){
            mockDb.rules = mockDb.rules.filter(r => r.id !== id);
            return mockDelay(true);
        }
        return request("DELETE", "file/attachment/rule/"+id);
    },
    /** action: "activate" | "deactivate" | "delete" */
    bulkRules(ids, action, source = null){
        if(isFarMockMode()){
            if(action === "delete"){
                mockDb.rules = mockDb.rules.filter(r => !ids.includes(r.id));
            }else{
                mockDb.rules.forEach(r => {
                    if(ids.includes(r.id)){ r.active = action === "activate" ? 1 : 0; r.updated_at = mockNow(); r.updated_by_name = "You"; }
                });
            }
            return mockDelay(true);
        }
        return request("POST", "file/attachment/rule/bulk", { data: { ids, action }, source });
    },
    getSettings(source = null){
        if(isFarMockMode()){
            return mockDelay(mockDb.settings).then(s => Object.assign({}, FAR_DEFAULT_SETTINGS, s));
        }
        return request("GET", "file/attachment/settings", { source }).then(body => Object.assign({}, FAR_DEFAULT_SETTINGS, (body && body.settings) || body || {}));
    },
    saveSettings(settings, source = null){
        if(isFarMockMode()){
            mockDb.settings = Object.assign({}, settings);
            return mockDelay(mockDb.settings);
        }
        return request("PUT", "file/attachment/settings", { data: { settings }, source });
    },
    /** params: { page, limit, search, action, result, from, to } → { items[], totalItemCount, totalPages } */
    getAuditLog(params = {}, source = null){
        if(isFarMockMode()){
            let items = mockDb.audit.slice();
            if(params.search){
                const q = String(params.search).toLowerCase();
                items = items.filter(i => [i.file_name, i.rule_name, i.recipient, i.user_name, i.reason, i.transfer_id].join(" ").toLowerCase().indexOf(q) >= 0);
            }
            if(params.action){
                items = items.filter(i => i.action === params.action);
            }
            if(params.result){
                items = items.filter(i => i.result === params.result);
            }
            return mockDelay({ items, totalItemCount: items.length, totalPages: 1 });
        }
        return request("GET", "file/attachment/audit", { params, source }).then(body => ({
            items: (body && (body.items || body.response)) || [],
            totalItemCount: (body && body.totalItemCount) || 0,
            totalPages: (body && body.totalPages) || 1
        }));
    }
};

export default FarApi;
